// "Add to Home Screen": which hint a visitor gets, the captured Android/Chrome install prompt, and the remembered
// dismissal. The pure parts (installMode) are tested in pwa.test.ts; the rest touches window and is guarded.

/** Chrome's install prompt event (not in the DOM typings). */
export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallMode = "ios" | "prompt" | null;

export type InstallEnv = {
  userAgent: string;
  /** already running from the home screen */
  standalone: boolean;
  /** a phone: a coarse pointer on a small screen */
  phone: boolean;
  /** Chrome handed us a beforeinstallprompt event */
  hasPrompt: boolean;
  dismissed: boolean;
};

/**
 * iOS Safari has no install prompt, so it gets the "Share, then Add to Home Screen" hint; Chrome and the other
 * browsers that fire beforeinstallprompt get an Install button. Never on a desktop, inside the installed app, after
 * a dismissal, or in an in-app browser (which can't add to the home screen).
 */
export function installMode(env: InstallEnv): InstallMode {
  if (env.standalone || !env.phone || env.dismissed) return null;
  if (env.hasPrompt) return "prompt";
  const ua = env.userAgent;
  const iPhone = /\b(iPhone|iPod)\b/.test(ua);
  const safari = /\bSafari\//.test(ua) && /\bVersion\//.test(ua) && !/\b(CriOS|FxiOS|EdgiOS|OPiOS|GSA|Instagram|FBAN|FBAV|Line)\b/.test(ua);
  return iPhone && safari ? "ios" : null;
}

const KEY = "cao.installHint.dismissed";

export function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberDismissal(): void {
  try {
    window.localStorage.setItem(KEY, "1");
  } catch {
    /* private mode or storage blocked: the hint just comes back next time */
  }
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches);
}

export function isPhone(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(pointer: coarse)").matches && Math.min(window.screen.width, window.screen.height) < 600;
}

// ---------------------------------------------------------------- the captured prompt (main.tsx starts listening)

let deferred: InstallPromptEvent | null = null;
let installed = false;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

/** Start listening before React mounts: Chrome fires beforeinstallprompt once, early. */
export function listenForInstallPrompt(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // no mini-infobar: the app shows its own, dismissible button
    deferred = e as InstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    installed = true;
    emit();
  });
}

export const installPrompt = () => deferred;
export const wasInstalled = () => installed;

export function subscribeInstall(f: () => void): () => void {
  subs.add(f);
  return () => subs.delete(f);
}

/** Show Chrome's install dialog. Resolves true if the user installed. */
export async function promptInstall(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  deferred = null; // a prompt event can be used once
  emit();
  try {
    await e.prompt();
    return (await e.userChoice).outcome === "accepted";
  } catch {
    return false;
  }
}
